import { useCallback, useEffect, useRef, useState } from "react";
import { getUpcomingPerformances, getUserAwards, getUserSpotlights, PublicPage } from "./profileApi";
import type { ProfileAward, ProfileContent } from "./profileContent";

export interface SectionRequest {
  loading: boolean;
  error: boolean;
  hasMore?: boolean;
  load: () => void;
}
export interface ProfileRequests {
  upcoming: SectionRequest;
  spotlights: SectionRequest;
  awards: SectionRequest;
}

function usePublicPage<T extends { id?: string; idx?: number }, C extends string | number>(handle: string, fetchPage: (handle: string, cursor?: C, signal?: AbortSignal) => Promise<PublicPage<T, C>>) {
  const [items, setItems] = useState<T[]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const state = useRef<{ controller?: AbortController; cursor?: C; busy: boolean }>({ busy: false });
  const load = useCallback(async () => {
    const current = state.current;
    if (current.busy) return;
    current.busy = true;
    const controller = new AbortController();
    current.controller = controller;
    setLoading(true);
    setError(false);
    try {
      const page = await fetchPage(handle, current.cursor, controller.signal);
      if (controller.signal.aborted || state.current !== current) return;
      // 중복 페이지 및 반복 커서 방지, 빈 마지막 페이지에서 종료
      const canContinue = page.hasMore && page.items.length > 0 && page.nextCursor !== undefined && page.nextCursor !== current.cursor;
      current.cursor = page.nextCursor;
      setItems(previous => {
        const unique = new Map((previous || []).map(item => [item.id ?? item.idx, item]));
        page.items.forEach(item => unique.set(item.id ?? item.idx, item));
        return [...unique.values()];
      });
      setHasMore(canContinue);
    } catch {
      if (!controller.signal.aborted && state.current === current) setError(true);
    } finally {
      if (!controller.signal.aborted && state.current === current) {
        current.busy = false;
        setLoading(false);
      }
    }
  }, [handle, fetchPage]);
  useEffect(() => {
    state.current = { busy: false };
    setItems(undefined);
    setHasMore(false);
    void load();
    return () => state.current.controller?.abort();
  }, [load]);
  return { items, loading, error, hasMore, load };
}

export function useProfileContent(handle: string) {
  const upcoming = usePublicPage(handle, getUpcomingPerformances);
  const spotlights = usePublicPage(handle, getUserSpotlights);
  const [awards, setAwards] = useState<ProfileAward[]>();
  const [userIdx, setUserIdx] = useState<number>();
  const [truncated, setTruncated] = useState(false);
  const [awardsLoading, setAwardsLoading] = useState(true);
  const [awardsError, setAwardsError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setAwards(undefined);
    setUserIdx(undefined);
    setTruncated(false);
    setAwardsLoading(true);
    setAwardsError(false);
    getUserAwards(handle, controller.signal).then(response => {
      if (controller.signal.aborted) return;
      setAwards(response.items);
      setUserIdx(response.userIdx);
      setTruncated(response.truncated);
    }).catch(() => {
      if (!controller.signal.aborted) setAwardsError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setAwardsLoading(false);
    });
    return () => controller.abort();
  }, [handle, attempt]);
  const content: ProfileContent = { upcoming: upcoming.items, spotlights: spotlights.items, awards, awardsTruncated: truncated };
  const requests: ProfileRequests = {
    upcoming, spotlights,
    awards: { loading: awardsLoading, error: awardsError, load: () => setAttempt(value => value + 1) },
  };
  return { content, requests, userIdx };
}
